import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

/** Space kept between the top of the capped sheet and the top of the visual viewport. */
export const KEYBOARD_SHEET_MARGIN_PX = 8;

/**
 * The sheet's max-height while the keyboard is up: the visual viewport's height less a margin,
 * when the visual viewport is shorter than the sheet's own height; null (no cap, the sheet's
 * class height applies) otherwise.
 */
export function keyboardSheetMaxHeight(viewportHeight: number, sheetHeight: number): number | null {
  if (viewportHeight >= sheetHeight) return null;
  return Math.max(0, viewportHeight - KEYBOARD_SHEET_MARGIN_PX);
}

/**
 * The focused field's row (`[data-pin-entry]`: field, error, Join) into view inside the sheet's
 * own scroller (`[data-pin-sheet-scroll]`) only: never the page, never the visual viewport. The
 * vendored react-aria scrollIntoView in vaul does the same; `Element.scrollIntoView` walks every
 * scroll ancestor up to the root, and on Chromium the root's scroll includes the visual viewport.
 */
function revealFocusedEntry(sheet: HTMLElement): void {
  const focused = document.activeElement;
  if (!(focused instanceof HTMLInputElement) || !sheet.contains(focused)) return;
  const scroller = focused.closest<HTMLElement>('[data-pin-sheet-scroll]');
  if (!scroller) return;
  const target = focused.closest<HTMLElement>('[data-pin-entry]') ?? focused;
  const t = target.getBoundingClientRect();
  const s = scroller.getBoundingClientRect();
  if (t.bottom > s.bottom) scroller.scrollTop += t.bottom - s.bottom;
  else if (t.top < s.top) scroller.scrollTop -= s.top - t.top;
}

/**
 * Keeps a tall sheet with a text input usable while the on-screen keyboard is up (TECH-663, the
 * Going PIN sheet). The sheet runs with `repositionInputs={false}`, so vaul does not lift it; and
 * on iOS the keyboard does not shrink `dvh`, so a 76dvh sheet keeps its height and the keyboard
 * covers its lower part. While `window.visualViewport` is shorter than the sheet, this returns a
 * max-height that fits the visual viewport, so the sheet's body scrolls inside itself; it returns
 * null once the keyboard closes, and the class height applies again. The focused PIN row is
 * revealed inside the sheet's own scroller: on focus, and once per keyboard opening after the cap
 * has laid out.
 *
 * Only `resize` is read. A visualViewport `scroll` (Safari panning to reveal the field, a
 * pinch-zoomed reader panning) never changes the height, and re-scrolling on it fights the
 * person's own pan. The sheet's height is read only while uncapped, so a capped sheet is compared
 * with its full height, not with the cap. No-op where `visualViewport` is missing.
 *
 * Per platform: iOS Safari/PWA and the Capacitor iOS WKWebView (no @capacitor/keyboard) shrink only
 * the visual viewport, so the cap applies. Android Chrome (`interactive-widget=resizes-content`)
 * and the Capacitor Android WebView (resized by @capacitor-community/safe-area) shrink the layout
 * viewport with the keyboard, so `76dvh` follows it, the cap stays null, and only the focus reveal
 * runs.
 */
export function useKeyboardSheetCap(sheetRef: RefObject<HTMLElement | null>): number | null {
  const [maxHeight, setMaxHeight] = useState<number | null>(null);

  useEffect(() => {
    const viewport = typeof window === 'undefined' ? undefined : window.visualViewport;
    if (!viewport) return;
    // The sheet's height while uncapped. The content is portalled, so the ref is read per event.
    let fullHeight = 0;
    let capped = false;

    const onResize = () => {
      const sheet = sheetRef.current;
      if (!sheet) return;
      if (!capped) fullHeight = sheet.getBoundingClientRect().height;
      // The unzoomed visible height: a pinch-zoom shrinks `height` (layout px) by 1/scale with no
      // keyboard at all, and must not cap the sheet under a zoomed reader.
      const next = keyboardSheetMaxHeight(viewport.height * viewport.scale, fullHeight);
      capped = next !== null;
      setMaxHeight(next);
    };
    const onFocusIn = () => {
      const sheet = sheetRef.current;
      if (sheet) revealFocusedEntry(sheet);
    };

    viewport.addEventListener('resize', onResize);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      viewport.removeEventListener('resize', onResize);
      document.removeEventListener('focusin', onFocusIn);
    };
  }, [sheetRef]);

  // Once per keyboard opening, after the cap is committed and laid out: a reveal from the resize
  // listener would measure the uncapped sheet, and a rAF can run before React commits the state.
  const wasCappedRef = useRef(false);
  useLayoutEffect(() => {
    const isCapped = maxHeight !== null;
    const sheet = sheetRef.current;
    if (isCapped && !wasCappedRef.current && sheet) revealFocusedEntry(sheet);
    wasCappedRef.current = isCapped;
  }, [maxHeight, sheetRef]);

  return maxHeight;
}
